/**
 * Common form types and configurations used across the application.
 * Defines interfaces and classes for form control configuration and validation.
 */

/**
 * Interface for select options used in dropdown and radio components.
 */
export interface SelectOption {
  /** @remarks The value that will be submitted when this option is selected */
  value: string;
  /** @remarks The display text shown to the user for this option */
  label: string;
  /** @remarks Optional flag to disable this option from selection */
  disabled?: boolean;
}

/**
 * Comprehensive control configuration interface for all form components.
 *
 * @remarks Components use the type property to determine which properties are relevant.
 * This interface provides a unified configuration structure for all form control types
 * including text inputs, selects, checkboxes, radio buttons, and textareas.
 */
export interface ControlConfig {
  /** @remarks Unique identifier for the form control */
  name: string;
  /** @remarks Display label for the form control */
  label: string;
  /** @remarks Type of form control to render */
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'radio' | 'checkbox' | 'select' | 'textarea';
  /** @remarks Whether this field is required for form submission */
  required?: boolean;
  /** @remarks Whether this field is disabled and non-interactive */
  disabled?: boolean;
  /** @remarks General error message for validation failures */
  errorMessage?: string;
  /** @remarks Specific error message when required field is empty */
  errorMessageRequired?: string;
  /** @remarks Placeholder text shown when field is empty */
  placeholder?: string;
  /** @remarks Array of options for select and radio controls */
  options?: SelectOption[];
  /** @remarks Text content for checkbox labels (supports HTML) */
  checkboxText?: string;
  /** @remarks Default boolean value for checkbox controls */
  value?: boolean;
  /** @remarks Minimum value for number inputs */
  min?: number;
  /** @remarks Maximum value for number inputs */
  max?: number;
  /** @remarks Step increment for number inputs */
  step?: number;
  /** @remarks Number of visible rows for textarea controls */
  rows?: number;
  /** @remarks Maximum character length for text inputs */
  maxLength?: number;
  /** @remarks Format specification for specialized inputs */
  format?: string;
  /** @remarks Icon identifier for input decoration */
  icon?: string;
}

/**
 * Class for form control configuration with default values.
 *
 * @remarks Used primarily by the form generation components to provide
 * a standardized configuration object with proper defaults for all form control types.
 * Converts the interface-based configuration into a class with explicit null handling.
 */
export class FormControlConfig {
  /** @remarks Unique identifier for the form control */
  name: string;
  /** @remarks Display label for the form control, null if not provided */
  label: string | null;
  /** @remarks Whether this field is required for form submission */
  required: boolean;
  /** @remarks General error message for validation failures, null if not provided */
  errorMessage: string | null;
  /** @remarks Specific error message when required field is empty, null if not provided */
  errorMessageRequired: string | null;
  /** @remarks Placeholder text shown when field is empty, null if not provided */
  placeholder: string | null;
  /** @remarks Type of form control to render, null if not specified */
  type: 'text' | 'email' | 'password' | 'number' | 'tel' | 'radio' | 'checkbox' | 'select' | 'textarea' | null;
  /** @remarks Array of options for select and radio controls, null if not applicable */
  options: SelectOption[] | null;
  /** @remarks Text content for checkbox labels, null if not applicable */
  checkboxText: string | null;
  /** @remarks Default boolean value for checkbox controls, null if not applicable */
  value: boolean | null;
  /** @remarks Minimum value for number inputs, null if not specified */
  min: number | null;
  /** @remarks Maximum value for number inputs, null if not specified */
  max: number | null;
  /** @remarks Step increment for number inputs, null if not specified */
  step: number | null;
  /** @remarks Number of visible rows for textarea controls, null if not specified */
  rows: number | null;
  /** @remarks Maximum character length for text inputs, null if not specified */
  maxLength: number | null;
  /** @remarks Format specification for specialized inputs, null if not specified */
  format: string | null;

  /**
   * Creates an instance of FormControlConfig.
   *
   * @remarks Initializes the form control configuration with provided values
   * and sets appropriate defaults for optional properties using nullish coalescing.
   *
   * @param {ControlConfig} config - The configuration object to initialize from
   */
  constructor(config: ControlConfig) {
    this.name = config.name;
    this.label = config.label ?? null;
    this.required = config.required ?? false;
    this.errorMessage = config.errorMessage ?? null;
    this.errorMessageRequired = config.errorMessageRequired ?? null;
    this.placeholder = config.placeholder ?? null;
    this.type = config.type ?? null;
    this.options = config.options ?? null;
    this.checkboxText = config.checkboxText ?? null;
    this.value = config.value ?? null;
    this.min = config.min ?? null;
    this.max = config.max ?? null;
    this.step = config.step ?? null;
    this.rows = config.rows ?? null;
    this.maxLength = config.maxLength ?? null;
    this.format = config.format ?? null;
  }
}
